// src/pages/ProjectPage.js - 섹션별 그룹 + 상태 연결 스타일

import React, { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAppContext } from "../context/AppContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faArrowRight,
  faFolder,
  faPlus,
  faEllipsisVertical,
  faTrash,
  faEdit,
  faSpinner,
  faChevronLeft,
} from "@fortawesome/free-solid-svg-icons";
import { Button, DeleteConfirmModal, Icon24, Icon32, Toast } from "../components/ui";
import WorkspaceLayout from "../components/WorkspaceLayout"; // [추가] 공통 레이아웃
import B2BLayout from "../components/b2b/B2BLayout"; // [B2B] B2B 레이아웃
import UpgradeModal from "../components/UpgradeModal";
import NewStudyModal from "../components/NewStudyModal";
import "./ProjectPage.scss";
import { updateProject } from "../services/api";
import { apiFetch, apiJson } from "../services/apiClient";
import { usePageHeader } from "../context/PageHeaderContext";
import { useB2BMode } from "../hooks/useB2BMode";

const ProjectPage = () => {
  const { projectSlug } = useParams(); // slug로 변경
  const navigate = useNavigate();

  // [B2B] B2B 모드 체크 (향후 tier 기반으로 대체 예정)
  const { isB2BMode } = useB2BMode();
  const Layout = isB2BMode ? B2BLayout : WorkspaceLayout;

  // [Supabase 연동] Context에서 프로젝트/조사 관리 함수 가져오기
  const {
    projects,
    getStudiesByProject,
    loadStudiesByProject, // [추가] 조사 목록 로드 함수
    loadProjects, // [추가] 전체 프로젝트/스터디 재로드 함수
    createStudy,
    canCreateStudy,
    getStudyLimit,
    isLoggedIn,
    sessionChecked,
  } = useAppContext();

  const [isCollapsed, setIsCollapsed] = useState(true); // 사이드바 접힘 상태
  // 각 연구별 아티팩트 상태
  // - undefined: 아직 studyId 키가 준비되지 않음
  // - null: 로딩/에러 등으로 "unknown" (없음으로 취급하면 UX 깨짐)
  // - Array: 정상 로드됨(없으면 빈 배열)
  const [studyArtifacts, setStudyArtifacts] = useState({});
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    study: null,
  }); // 삭제 모달 상태
  const [deleteSuccessToast, setDeleteSuccessToast] = useState({
    isOpen: false,
    studyName: ''
  }); // 삭제 완료 토스트 상태
  const [openMenuStudyId, setOpenMenuStudyId] = useState(null); // 열린 메뉴의 연구 ID
  const [upgradeModal, setUpgradeModal] = useState({
    isOpen: false,
    reason: 'study'
  }); // 업그레이드 모달 상태
  const [projectStudies, setProjectStudies] = useState([]); // 프로젝트별 조사 목록 (직접 관리)
  const [studiesLoading, setStudiesLoading] = useState(false); // 조사 로딩 상태 (직접 관리)
  const [project, setProject] = useState(null); // slug로 조회한 프로젝트
  const [projectLoading, setProjectLoading] = useState(true); // 프로젝트 로딩 상태

  useEffect(() => {
    if (!sessionChecked || !isLoggedIn) return;
    loadProjects();
  }, [isLoggedIn, sessionChecked, loadProjects]);


  // [추가] slug로 프로젝트 조회
  useEffect(() => {
    const loadProject = async () => {
      if (!projectSlug) return;

      setProjectLoading(true);

      try {
        const projectData = await apiJson(`/projects/by-slug/${projectSlug}`, {
          method: "GET",
        });
        setProject(projectData);
      } catch (error) {
        console.error("프로젝트 로드 오류:", error);
        setProject(null);
      } finally {
        setProjectLoading(false);
      }
    };

    loadProject();
  }, [projectSlug]);

  // [통합] 프로젝트 진입 시 조사 목록 + 아티팩트 상태 모두 로드 (완료 후 렌더링)
  useEffect(() => {
    const loadProjectStudiesAndArtifacts = async () => {
      if (!project || !project.id) return;

      setStudiesLoading(true);

      try {
        // 1. 조사 목록 로드
        const response = await apiFetch(`/projects/${project.id}/studies`, {
          method: "GET",
        });

        let studies = [];
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            studies = data.studies || [];
          } else {
            console.error("조사 목록 로드 실패:", data.error);
          }
        } else {
          console.error("API 호출 실패:", response.status);
        }

        // 2. 모든 조사의 아티팩트를 병렬로 로드
        const artifactsMap = {};
        if (studies.length > 0) {
          // 우선 전부 unknown으로 세팅(개별 요청 실패 시 '없음'으로 오해 방지)
          studies.forEach((s) => {
            artifactsMap[s.id] = null;
          });

          // 일시 장애(500 등) 대비: 3회까지 자동 재시도하면서 unknown(spinner)을 유지한다.
          const fetchArtifactsBatch = async (targetStudies, attempt = 0) => {
            const results = await Promise.all(
              targetStudies.map(async (study) => {
                try {
                  const artifactResponse = await apiFetch(`/studies/${study.id}/artifacts`, {
                    method: "GET",
                  });
                  if (artifactResponse.ok) {
                    const artifactData = await artifactResponse.json();
                    return { studyId: study.id, ok: true, artifacts: artifactData.artifacts || [] };
                  }
                  return { studyId: study.id, ok: false, artifacts: null };
                } catch (e) {
                  return { studyId: study.id, ok: false, artifacts: null };
                }
              })
            );

            const failedIds = [];
            results.forEach((r) => {
              if (r.ok) {
                artifactsMap[r.studyId] = r.artifacts;
              } else {
                // keep null(unknown)
                failedIds.push(r.studyId);
              }
            });

            if (failedIds.length > 0 && attempt < 2) {
              const delayMs = attempt === 0 ? 250 : attempt === 1 ? 700 : 1400;
              await new Promise((res) => setTimeout(res, delayMs));
              const retryStudies = targetStudies.filter((s) => failedIds.includes(s.id));
              return fetchArtifactsBatch(retryStudies, attempt + 1);
            }
            return null;
          };

          await fetchArtifactsBatch(studies, 0);
        }

        // 3. 모든 데이터가 준비된 후에만 상태 업데이트
        setProjectStudies(studies);
        setStudyArtifacts(artifactsMap);
      } catch (error) {
        console.error("조사 및 아티팩트 로드 오류:", error);
        setProjectStudies([]);
        setStudyArtifacts({});
      } finally {
        setStudiesLoading(false);
      }
    };

    loadProjectStudiesAndArtifacts();
  }, [project]);

  // 외부 클릭 시 메뉴 닫기
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenMenuStudyId(null);
    };

    if (openMenuStudyId) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [openMenuStudyId]);

  // 아티팩트 상태 확인 함수
  const getArtifactStatus = (studyId, artifactType) => {
    const artifacts = studyArtifacts[studyId];
    if (artifacts == null) return null; // unknown
    return (artifacts || []).find((artifact) => artifact.artifact_type === artifactType);
  };

  // 아티팩트 아이콘 렌더링 함수
  const renderArtifactIcon = (studyId, artifactType, label) => {
    const artifact = getArtifactStatus(studyId, artifactType);
    // studyId로 study 정보 찾기 (slug 가져오기 위해)
    const study = projectStudies.find(s => s.id === studyId);
    const studySlug = study?.slug || studyId;

    if (artifact === null) {
      // unknown (로딩/에러) 상태
      return (
        <span className="artifact-icon loading" title={`${label} 상태 확인 중`} aria-disabled="true">
          <FontAwesomeIcon icon={faSpinner} spin />
        </span>
      );
    }

    if (artifact) {
      // 생성된 경우 - 상태에 따라 다르게 처리
      if (artifact.status === "completed") {
        // 완료된 경우 - 클릭 가능 (slug 사용)
        return (
          <Link
            to={`/study/${studySlug}/artifact/${artifact.id}?type=${encodeURIComponent(artifactType)}`}
            className="artifact-icon completed"
            title={`${label} 보기`}
          >
            <Icon32 name="checkFill" />
          </Link>
        );
      } else {
        // pending 상태 - 클릭 불가
        return (
          <span
            className="artifact-icon pending"
            title={`${label} 생성 중`}
            aria-disabled="true"
          >
            <Icon32 name="checkDisable" />
          </span>
        );
      }
    } else {
      // 생성 전 - checkDisable 아이콘
      return (
        <span
          className="artifact-icon not-created"
          title={`${label} 미생성`}
          aria-disabled="true"
        >
          <Icon32 name="checkDisable" />
        </span>
      );
    }
  };

  // 연구 삭제 함수
  const handleDeleteStudy = async (study) => {
    try {
      const response = await apiFetch(`/studies/${study.id}`, {
        method: "DELETE",
      });

      if (response.ok) {
        try {
          const result = await response.json();
          // 삭제 성공 시 연구 목록에서 제거
          setProjectStudies((prev) => prev.filter((s) => s.id !== study.id));
          
          // ⭐ 전체 프로젝트와 스터디 재로드 (tier 제한이 즉시 반영됨)
          await loadProjects({ force: true });
          
          setDeleteModal({ isOpen: false, study: null });
          // 삭제 완료 토스트 표시
          setDeleteSuccessToast({
            isOpen: true,
            studyName: study.name
          });
        } catch (jsonError) {
          // 응답이 비어있어도 삭제는 성공한 것으로 처리
          setProjectStudies((prev) => prev.filter((s) => s.id !== study.id));
          
          // ⭐ 전체 프로젝트와 스터디 재로드 (tier 제한이 즉시 반영됨)
          await loadProjects({ force: true });
          
          setDeleteModal({ isOpen: false, study: null });
          // 삭제 완료 토스트 표시
          setDeleteSuccessToast({
            isOpen: true,
            studyName: study.name
          });
        }
      } else {
        try {
          const errorData = await response.json();
          console.error("연구 삭제 실패:", errorData);
          alert(`삭제 실패: ${errorData.error || "알 수 없는 오류"}`);
        } catch (jsonError) {
          console.error(
            "연구 삭제 실패 (JSON 파싱 오류):",
            response.status,
            response.statusText
          );
          alert(`삭제 실패: ${response.status} ${response.statusText}`);
        }
      }
    } catch (error) {
      console.error("연구 삭제 오류:", error);
      alert("삭제 중 오류가 발생했습니다.");
    }
  };

  // 케밥 메뉴 토글
  const toggleMenu = (studyId, e) => {
    e.stopPropagation();

    if (openMenuStudyId === studyId) {
      setOpenMenuStudyId(null);
      return;
    }

    setOpenMenuStudyId(studyId);

    // 드롭다운 위치 조정을 위해 다음 프레임에서 실행
    setTimeout(() => {
      const dropdown = document.querySelector(
        `[data-study-id="${studyId}"] .dropdown-menu`
      );
      if (dropdown) {
        const rect = dropdown.getBoundingClientRect();
        const viewportHeight = window.innerHeight;

        // 드롭다운이 화면 하단을 벗어나는 경우 위로 올리기
        if (rect.bottom > viewportHeight - 20) {
          dropdown.classList.add("dropup");
        } else {
          dropdown.classList.remove("dropup");
        }
      }
    }, 0);
  };

  // 메뉴 닫기 (외부 클릭 시)
  const closeMenu = () => {
    setOpenMenuStudyId(null);
  };

  // 삭제 모달 열기
  const openDeleteModal = (study) => {
    setDeleteModal({ isOpen: true, study });
    setOpenMenuStudyId(null); // 메뉴 닫기
  };

  // 삭제 모달 닫기
  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, study: null });
  };

  // 새 리서치 생성 방식 선택 모달
  const [showNewStudyModal, setShowNewStudyModal] = useState(false);

  // 프로젝트명 수정 함수
  const handleProjectNameSave = async (newValue) => {
    if (!newValue || !newValue.trim()) {
      alert("프로젝트명을 입력해주세요.");
      return;
    }

    try {
      const result = await updateProject(project.id, {
        name: newValue.trim()
      });

      if (result.success) {
        const newSlug = result.data?.slug;
        
        // ⭐ slug가 변경되었으면 URL 업데이트
        if (newSlug && newSlug !== projectSlug) {
          navigate(`/project/${newSlug}`, { replace: true });
        } else {
          // slug가 변경되지 않았으면 프로젝트 정보만 업데이트
          setProject(prev => ({ ...prev, ...result.data }));
        }
      } else {
        alert("프로젝트명 수정에 실패했습니다: " + (result.error || "알 수 없는 오류"));
        throw new Error(result.error || "알 수 없는 오류");
      }
    } catch (error) {
      console.error("프로젝트명 수정 오류:", error);
      alert("프로젝트명 수정 중 오류가 발생했습니다.");
      throw error;
    }
  };

  const handleProjectNameCancel = () => {
    // 취소 시 별도 처리 없음 (PageHeader에서 자동 처리)
  };

  // 헤더 설정 (project 로딩 중에도 기본 헤더를 먼저 세팅해 "헤더가 늦게 뜨는" UX를 줄인다)
  usePageHeader(
    {
      title: project?.name || (projectLoading ? "로딩 중..." : "프로젝트"),
      subtitle: project?.keywords?.length ? (
        <div className="page-header-tags">
          {project.keywords.map((tag, index) => (
            <span key={index} className="page-header-tag">
              {tag}
            </span>
          ))}
        </div>
      ) : (
        ""
      ),
      showBack: true,
      onBack: () => navigate("/research"),
      actions: project?.created_at ? (
        <div style={{ color: "#9ca3af", fontSize: 14, fontWeight: 500 }}>
          생성일: {new Date(project.created_at).toLocaleDateString("ko-KR")}
        </div>
      ) : null,
      editableTitle:
        project && !projectLoading
          ? {
              value: project.name || "",
              onSave: handleProjectNameSave,
              onCancel: handleProjectNameCancel,
            }
          : null,
    },
    [project?.id, project?.name, project?.created_at, projectLoading, projectSlug, isB2BMode, project?.keywords?.join?.(",")]
  );

  // [엠티 케이스] 로딩 중이거나 프로젝트가 없으면 안내
  if (projectLoading) {
    return (
      <Layout hideCollapseButton={false} defaultCollapsed={true}>
        <div
          className="empty-studies-table"
          style={{ padding: "40px", textAlign: "center" }}
        >
          <p>로딩 중...</p>
        </div>
      </Layout>
    );
  }

  if (!project) {
    return (
      <Layout hideCollapseButton={false} defaultCollapsed={true}>
        <div
          className="empty-studies-table"
          style={{ padding: "40px", textAlign: "center" }}
        >
          <p>프로젝트를 찾을 수 없습니다.</p>
          <Link to="/research">워크스페이스로 돌아가기</Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout hideCollapseButton={false} defaultCollapsed={!isB2BMode}>
      {/* [조사 목록] */}
      <main style={{ padding: "32px" }}>
        <section className="project-studies-section">
          <div className="section-header">
            <h2>리서치 목록</h2>
            <Button
              className="btn btn-create btn-primary"
              variant="primary"
              leftIcon={<FontAwesomeIcon icon={faPlus} />}
              onClick={() => {
                if (!canCreateStudy(project.id)) {
                  setUpgradeModal({ isOpen: true, reason: 'study' });
                  return;
                }
                setShowNewStudyModal(true);
              }}
            >
              새 리서치 생성
            </Button>
          </div>

          {/* [변경] 로딩 상태 처리 + 빈 케이스 처리 */}
          {studiesLoading ? (
            <div className="empty-studies-table">
              <FontAwesomeIcon icon={faSpinner} spin style={{ color: '#3A66FF', fontSize: '24px', marginBottom: '12px' }} />
              <p style={{ color: '#3A66FF' }}>리서치 목록을 불러오는 중...</p>
            </div>
          ) : projectStudies.length > 0 ? (
            <div className="studies-table">
              <div className="studies-table-header">
                <div className="studies-header-item">리서치명</div>
                <div className="studies-header-item">리서치 계획서</div>
                <div className="studies-header-item">스크리닝 설문</div>
                <div className="studies-header-item">진행 가이드</div>
                <div className="studies-header-item">최근 수정일</div>
                <div className="studies-header-item"></div>{" "}
                {/* '조사 보기' 버튼을 위한 빈 헤더 */}
              </div>
              {/* [변경] DB 컬럼명: project_id, created_at, initial_input 등 */}
              {projectStudies.map((study) => (
                <div
                  key={study.id}
                  className="studies-table-row"
                  data-study-id={study.id}
                >
                  <div className="row-item title">{study.name}</div>
                  <div className="row-item artifact">
                    {renderArtifactIcon(study.id, "plan", "계획서")}
                  </div>
                  <div className="row-item artifact">
                    {renderArtifactIcon(study.id, "survey", "스크리닝 설문")}
                  </div>
                  <div className="row-item artifact">
                    {renderArtifactIcon(study.id, "guideline", "진행 가이드")}
                  </div>
                  <div className="row-item date">
                    {study.created_at
                      ? new Date(study.created_at).toLocaleDateString("ko-KR")
                      : "-"}
                  </div>
                  <div className="row-item actions">
                    <div className="actions-container">
                      <Link
                        to={`/study/${study.slug || study.id}`}
                        className="view-study-btn"
                      >
                        보기 <FontAwesomeIcon icon={faArrowRight} />
                      </Link>
                      <div className="kebab-menu">
                        <button
                          className="kebab-btn"
                          onClick={(e) => toggleMenu(study.id, e)}
                          title="더보기"
                        >
                          <FontAwesomeIcon icon={faEllipsisVertical} />
                        </button>
                        {/* 드롭다운 메뉴 */}
                        {openMenuStudyId === study.id && (
                          <div className="dropdown-menu">
                            <button
                              className="menu-item delete"
                              onClick={(e) => openDeleteModal(study)}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                              <span>리서치 삭제</span>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-studies-table">
              <p>아직 생성된 리서치가 없습니다.</p>
            </div>
          )}
        </section>

        {/* 새 리서치 생성 모달 */}
        <NewStudyModal
          isOpen={showNewStudyModal}
          onClose={() => setShowNewStudyModal(false)}
          projectSlug={project?.slug}
        />

        {/* 삭제 확인 모달 */}
        <DeleteConfirmModal
          isOpen={deleteModal.isOpen}
          title="리서치 삭제"
          itemName={deleteModal.study?.name || ''}
          onConfirm={() => handleDeleteStudy(deleteModal.study)}
          onCancel={closeDeleteModal}
        />

        <Toast
          isOpen={deleteSuccessToast.isOpen}
          type="success"
          message={`"${deleteSuccessToast.studyName}" 리서치를 삭제했습니다.`}
          onClose={() => setDeleteSuccessToast({ isOpen: false, studyName: '' })}
        />

        {/* 업그레이드 모달 */}
        <UpgradeModal
          isOpen={upgradeModal.isOpen}
          reason={upgradeModal.reason}
          onClose={() => setUpgradeModal({ isOpen: false, reason: 'study' })}
        />
      </main>
    </Layout>
  );
};

export default ProjectPage;
